import { z } from 'zod';
import { MEMBER_NAME_MAX_LENGTH } from '../../config.ts';
import { counted, idSchema } from '../primitives.ts';
import { REVIEW_STATUSES, type ReviewStatus } from './review.ts';
import {
  NO_OVERRIDES,
  namesAnOverride,
  resolveTerms,
  type TermOverrides,
  type Terms,
  termOverridesSchema,
} from './terms.ts';

const EMAIL_MAX_LENGTH = 254;

export const MEMBER_STATUSES = REVIEW_STATUSES;
export type MemberStatus = ReviewStatus;

/** Lives here, not beside its query, because the page imports it and may not import queries. */
export const MEMBER_FILTERS = ['pending', 'approved', 'rejected', 'all'] as const;
export type MemberFilter = (typeof MEMBER_FILTERS)[number];

export function approvedMember<T extends { status: MemberStatus }>(
  member: T | undefined,
  named: string,
): { ok: true; member: T } | { ok: false; violations: string[] } {
  if (!member) return { ok: false, violations: [`member ${named} not found`] };
  if (member.status !== 'APPROVED') {
    return { ok: false, violations: [`member ${named} is not approved (${member.status})`] };
  }
  return { ok: true, member };
}

/** Keyed on `BookRef`, never the book name alone. */
export interface BookTerms extends TermOverrides {
  /** Filters recommendations, not positions (see offer.ts). Not a term: does not inherit. */
  muted: boolean;
}

const DEFAULT_BOOK_TERMS: BookTerms = { muted: false, ...NO_OVERRIDES };

export const bookTermsOf = (said: BookTerms | undefined): BookTerms => said ?? DEFAULT_BOOK_TERMS;

/** Such a book is stored as no row at all. */
export const isDefaultBook = (said: BookTerms): boolean =>
  (Object.keys(DEFAULT_BOOK_TERMS) as (keyof BookTerms)[]).every(
    (field) => said[field] === DEFAULT_BOOK_TERMS[field],
  );

export const hears = (said: BookTerms | undefined): boolean => !bookTermsOf(said).muted;

/**
 * The autopilot card and the order round must both use this, or a member is
 * promised trades that never happen. Pause and mute outrank autopilot.
 */
export const flies = (
  member: TermOverrides & { autopilotPausedAt: Date | null },
  said: BookTerms | undefined,
): boolean =>
  member.autopilotPausedAt === null &&
  hears(said) &&
  resolveTerms(member, bookTermsOf(said)).autopilot;

export function termsIn(
  member: TermOverrides,
  said: BookTerms | undefined,
): { muted: boolean } & Terms {
  const book = bookTermsOf(said);
  return { muted: book.muted, ...resolveTerms(member, book) };
}

/** Shape check only: Google verifies the address. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(EMAIL_MAX_LENGTH)
  .refine((email) => email.includes('@') && !email.startsWith('@') && !email.endsWith('@'), {
    message: 'must be an email address',
  });

export const memberNameSchema = z.string().trim().min(1).max(MEMBER_NAME_MAX_LENGTH);

export const memberIdSchema = idSchema;

interface IdentityChanges {
  googleSub?: string;
  name?: string;
  picture?: string;
}

/**
 * What a sign-in changes on the member row it matched: the subject once, and a name or picture
 * Google gave anew. A null from Google removes nothing: absence is not "none".
 */
export function identityChanges(
  row: { googleSub: string | null; name: string | null; picture: string | null },
  identity: { subject: string; name: string | null; picture: string | null },
): IdentityChanges | null {
  const changes: IdentityChanges = {};
  if (row.googleSub === null) changes.googleSub = identity.subject;
  if (identity.name !== null && identity.name !== row.name) changes.name = identity.name;
  if (identity.picture !== null && identity.picture !== row.picture) {
    changes.picture = identity.picture;
  }
  return Object.keys(changes).length > 0 ? changes : null;
}

export const bookTermsSchema = termOverridesSchema.extend({ muted: z.boolean().optional() });
export type BookTermsAsked = z.infer<typeof bookTermsSchema>;

export const namesATerm = (terms: BookTermsAsked): boolean =>
  terms.muted !== undefined || namesAnOverride(terms);

export interface Standing {
  isAdmin: boolean;
  status: MemberStatus | null;
}

export function canRead(standing: Standing): boolean {
  return standing.isAdmin || standing.status === 'APPROVED';
}

export function canWrite(standing: Standing): boolean {
  return standing.isAdmin;
}

/** What deleting an account would strand, counted under the member's lock. */
export interface ErasureStanding {
  openPositions: number;
  /** Sent or live at their broker: the desk has not heard how they end. */
  standingOrders: number;
  analyst: { name: string; followers: number; recommendations: number } | null;
}

/**
 * An account goes whole or not at all, so what outlives it ends first: a trade at their broker
 * that nobody would watch, and a record other members read and hold positions on.
 */
export function validateErasure(standing: ErasureStanding): string[] {
  const violations: string[] = [];
  const { openPositions, standingOrders, analyst } = standing;
  if (openPositions > 0) {
    violations.push(
      `you hold ${counted(openPositions, 'open position', 'open positions')} — exit first, ` +
        'so nothing is left at your broker unwatched',
    );
  }
  if (standingOrders > 0) {
    violations.push(
      `${counted(standingOrders, 'order', 'orders')} at your broker ` +
        `${standingOrders === 1 ? 'is' : 'are'} still pending — try again once settled`,
    );
  }
  if (analyst && (analyst.followers > 0 || analyst.recommendations > 0)) {
    const read = [
      analyst.followers > 0 ? counted(analyst.followers, 'follower', 'followers') : null,
      analyst.recommendations > 0
        ? counted(analyst.recommendations, 'recommendation', 'recommendations')
        : null,
    ].filter((part) => part !== null);
    violations.push(
      `you publish as ${analyst.name}, with ${read.join(' and ')} — ask the admins to ` +
        'stand the listing down first',
    );
  }
  return violations;
}

/** The address typed back, as the row keeps it: which account is meant is said twice. */
export const confirmsErasure = (member: { email: string }, typed: string): boolean =>
  typed.trim().toLowerCase() === member.email;

export function validateApply(standing: Standing): string[] {
  if (standing.isAdmin) return ['you are an admin — you already have full access'];
  if (standing.status === 'PENDING') return ['your request is already pending review'];
  if (standing.status === 'APPROVED') return ['you already have access'];
  return [];
}

export const inviteCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[0-9a-f]{12}$/, 'is not an invite code');

/** A friend waiting or let in holds one of their inviter's invites; an admin's no hands it back. */
export const holdsInvite = (member: { status: MemberStatus }): boolean =>
  member.status !== 'REJECTED';

export type InviteTally = Record<MemberStatus, number>;

export const NO_INVITES: InviteTally = { PENDING: 0, APPROVED: 0, REJECTED: 0 };

/** A seat in use is never taken back, even past the allowance. */
export const invitesLeft = (allowance: number, tally: InviteTally): number =>
  Math.max(
    0,
    allowance -
      MEMBER_STATUSES.filter((status) => holdsInvite({ status })).reduce(
        (held, status) => held + tally[status],
        0,
      ),
  );

export function otherInvites(tally: InviteTally, deciding: MemberStatus): string {
  const others = { ...tally, [deciding]: Math.max(0, tally[deciding] - 1) };
  const said = [
    others.APPROVED > 0 && `${others.APPROVED} let in`,
    others.PENDING > 0 && `${others.PENDING} waiting`,
    others.REJECTED > 0 && `${others.REJECTED} declined`,
  ].filter((part) => part !== false);
  return said.length === 0 ? 'this is their first' : said.join(' · ');
}

/** Never an email address. */
export const firstName = (name: string | null): string => name?.trim().split(/\s+/)[0] || 'Someone';

/** Whether an ask arriving on this link carries the inviter's name. */
export function vouches(
  arriving: { id: number | null; status: MemberStatus | null; invitedBy: number | null },
  inviter: { id: number; status: MemberStatus } | undefined,
  left: number,
): boolean {
  if (inviter?.status !== 'APPROVED' || inviter.id === arriving.id) return false;
  if (arriving.status === 'APPROVED') return false;
  // The first vouch on a waiting request stands; one an admin turned down was handed back.
  if (arriving.status === 'PENDING' && arriving.invitedBy !== null) return false;
  return left > 0;
}
